import React, { useState, useEffect, useCallback } from 'react';
import { StaticSRPPage } from './StaticSRPPage';
import type { StaticSRPData } from '../../../types/srp-static';

export interface SRPShellProps {
  /** Data for the static view (from pageContext.data) */
  data: StaticSRPData;
  locale: string;
  pathname: string;
  /** The full interactive tree (SavedState + SearchProvider + SearchWrapper + children). Rendered when user goes interactive. */
  interactiveTree: React.ReactNode;
}

/**
 * Option B: Renders static SRP first (no providers). When the user goes interactive
 * (requestIdleCallback or first interaction), mounts the full tree with all providers.
 */
export function SRPShell({ data, locale, pathname, interactiveTree }: SRPShellProps): React.ReactElement {
  const [isInteractive, setIsInteractive] = useState(false);

  const goInteractive = useCallback(() => {
    setIsInteractive(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onIdle = window.requestIdleCallback
      ? window.requestIdleCallback(goInteractive, { timeout: 4000 })
      : setTimeout(goInteractive, 2000);

    const onInteraction = (): void => {
      goInteractive();
      window.removeEventListener('pointerdown', onInteraction);
      window.removeEventListener('keydown', onInteraction);
      window.removeEventListener('focus', onInteraction, true);
      if (typeof onIdle === 'number') clearTimeout(onIdle);
      else if (typeof onIdle === 'object' && typeof (onIdle as any).cancel === 'function') (onIdle as any).cancel();
    };

    window.addEventListener('pointerdown', onInteraction, { once: true, passive: true });
    window.addEventListener('keydown', onInteraction, { once: true, passive: true });
    window.addEventListener('focus', onInteraction, { once: true, capture: true });

    return () => {
      window.removeEventListener('pointerdown', onInteraction);
      window.removeEventListener('keydown', onInteraction);
      window.removeEventListener('focus', onInteraction, true);
      if (typeof onIdle === 'number') clearTimeout(onIdle);
      else if (typeof onIdle === 'object' && typeof (onIdle as any).cancel === 'function') (onIdle as any).cancel();
    };
  }, [goInteractive]);

  if (isInteractive) {
    return <>{interactiveTree}</>;
  }

  return <StaticSRPPage data={data} locale={locale} pathname={pathname} />;
}
